import type { ComponentType, ReactNode } from 'react';
import type { IconProps } from '@phosphor-icons/react';
import { cx } from '../cx';
import { TONE_SOFT, type Tone } from '../tone';
import { Icon } from '../Icon';

/** The shared status vocabulary (ui/tone.ts): positive for a good sign (hiring fast), info for worth knowing, neutral for a plain fact. */
export type FactBadgeTone = Tone;
export type FactBadge = {
  id: string;
  tone: FactBadgeTone;
  icon: ComponentType<IconProps>;
  label: ReactNode;
  /** The basis for the badge ("12 new jobs in 30 days"). Required. */
  caption: ReactNode;
  /** Built from a publisher's data that needs their permission to show. */
  needsPermission?: boolean;
  /** That permission was given. A badge needing permission without it is never shown. */
  approved?: boolean;
};

/** The badges that may be shown, at most six. Pure, exported for tests. */
export const visibleBadges = (badges: FactBadge[]): FactBadge[] =>
  badges.filter((b) => !b.needsPermission || b.approved).slice(0, MAX_FACT_BADGES);

export type BadgeRailProps = {
  badges: FactBadge[];
  /** Accessible name of the list. */
  label: string;
  className?: string;
};

export const MAX_FACT_BADGES = 6;

/** Up to six fact badges about an employer, each with an icon tile, a label and its basis. */
export function BadgeRail({ badges, label, className }: BadgeRailProps) {
  return (
    <ul aria-label={label} className={cx('grid gap-3 sm:grid-cols-2 lg:grid-cols-3', className)}>
      {visibleBadges(badges).map((b) => (
        <li
          key={b.id}
          className="flex items-start gap-3 rounded-jc-info bg-jc-surface p-3 ring-1 ring-inset ring-jc-card-border"
        >
          <span
            aria-hidden="true"
            className={cx(
              'inline-flex size-10 shrink-0 items-center justify-center rounded-jc-info',
              TONE_SOFT[b.tone],
            )}
          >
            <Icon icon={b.icon} size="md" />
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="text-sm font-semibold text-jc-ink">{b.label}</span>
            <span className="text-xs text-jc-ink-secondary">{b.caption}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}
